import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Field, TextInput } from '@salonomy/ui';
import { ApiClientError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

export function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 2FA step-up state: set once /login returns a challenge (correct password, account is TOTP-enrolled).
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const outcome = await login(email.trim(), password);
      if (outcome.kind === 'twoFactor') {
        setChallengeId(outcome.challengeId);
        setCode('');
      }
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Could not sign in. Check your connection.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    setError(null);
    setBusy(true);
    try {
      const trimmed = code.trim();
      await verifyTwoFactor(
        challengeId,
        useRecovery ? { recoveryCode: trimmed } : { code: trimmed },
      );
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Could not verify the code. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__mark">
          Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
        </div>
        {challengeId ? (
          <>
            <div className="login__sub">
              Two-factor authentication. Enter the {useRecovery ? 'recovery code' : '6-digit code'}{' '}
              from your authenticator app.
            </div>
            <form className="login__form" onSubmit={onVerify}>
              {error ? <div className="login__error">{error}</div> : null}
              <Field
                label={useRecovery ? 'Recovery code' : 'Authentication code'}
                htmlFor="totp-code"
              >
                <TextInput
                  id="totp-code"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
                />
              </Field>
              <Button type="submit" size="lg" block disabled={busy}>
                {busy ? 'Verifying…' : 'Verify'}
              </Button>
              <button
                type="button"
                className="login__linkbtn"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                  setError(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--slm-cobalt)',
                }}
              >
                {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="login__sub">Sign in to your salon console.</div>
            <form className="login__form" onSubmit={onSubmit}>
              {error ? <div className="login__error">{error}</div> : null}
              <Field label="Email" htmlFor="email">
                <TextInput
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@salon.co.uk"
                />
              </Field>
              <Field label="Password" htmlFor="password">
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </Field>
              <Button type="submit" size="lg" block disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
              <a
                href={`${import.meta.env.BASE_URL.replace(/\/$/, '')}/forgot-password`}
                style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
              >
                Forgot password?
              </a>
            </form>
            <div className="login__hint">
              Demo salon — owner@marlow.test / salonomy-demo
              <br />
              No contracts, ever. Monthly rolling, one-click cancel.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
